import { createContext, useContext, type ReactNode, type RefObject } from "react";

import type { SelectCreator } from "./SelectCreateRow";

export interface SelectItemRecord {
  node: ReactNode;
  textValue: string;
  disabled?: boolean;
}

export interface SelectContextValue {
  multiple: boolean;
  /** The single-mode value (`undefined` in multi mode). */
  value: string | undefined;
  /** Every selected value — zero or one in single mode. */
  selectedValues: string[];
  isSelected: (value: string) => boolean;
  // Method syntax keeps these bivariant, so the root can supply handlers narrowed to its `TValue`.
  selectItem(value: string): void;
  removeValue(value: string): void;
  fallbackLabel?(value: string): ReactNode;
  /** The best label for a value: its rendered item's node, then `fallbackLabel`, then the raw value. */
  labelFor: (value: string) => ReactNode;
  maxCountReached: boolean;
  maxDisplayCount: number | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled: boolean;
  modal: boolean;
  clearable: boolean;
  clear: () => void;
  itemCount: number;
  registerItem: (value: string) => () => void;
  rememberItem: (value: string, record: SelectItemRecord) => void;
  getItem: (value: string) => SelectItemRecord | undefined;
  isRendered: (value: string) => boolean;
  /** True when some rendered item's value or text equals `text`, ignoring case. */
  hasItemMatching: (text: string) => boolean;
  /** Set when the dropdown closes and focus is about to return to the trigger, so `openOnFocus` ignores that focus. */
  suppressFocusOpen: RefObject<boolean>;
  /** Set when a selection closed the dropdown, so the creator row's `commitOnClose` doesn't also commit. */
  closedBySelection: RefObject<boolean>;
  creator: true | SelectCreator | undefined;
}

export const SelectContext = createContext<SelectContextValue | null>(null);

export const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) throw new Error("Select parts must be rendered inside <Select>.");
  return context;
};

/** True inside the hidden collector that keeps `Select.Content`'s children mounted while the list isn't showing them; parts render nothing there. */
export const SelectCollectingContext = createContext(false);

/** True for the rows `SelectPinnedItems` renders: they stand in for unrendered items, so they must not register as rendered. */
export const SelectPinnedContext = createContext(false);

export interface SelectContentContextValue {
  hideSelectedItems: boolean;
  setSearch: (search: string) => void;
  /** The creator row registers its commit here while its row is showing, for `commitOnClose`. */
  pendingCommit: RefObject<(() => void) | null>;
}

export const SelectContentContext = createContext<SelectContentContextValue | null>(null);
